/**
 * provetrail.org homepage hero: verify a real Provetrail record in the visitor's
 * own browser. This runs the actual published `provetrail` npm package (its COSE +
 * RFC 6962 Merkle logic) against a real conformance vector. No server, nothing
 * uploaded. Tampering with a single byte breaks the proof, which is the point.
 */
import "./shim/buffer-global";
import { verifyRun, VerifyError } from "provetrail";
import { version as verifierVersion } from "provetrail/package.json";

interface Embedded {
  record_b64: string;
  public_key_hex: string;
  key_id: string;
  origin: string;
  events: number;
}

const b64ToBytes = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const hexToBytes = (s: string) =>
  Uint8Array.from(s.match(/../g)!.map((b) => parseInt(b, 16)));
const bytesToHex = (b: Uint8Array) =>
  Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");

// Every byte of the record is covered: event bytes are hashed into the signed
// Merkle root and the checkpoint is signed, so flipping ANY byte breaks
// verification. Which check reports it (root mismatch, bad signature, decode
// error) depends on where the flip lands, and that variety is part of the demo.
const HEX_WINDOW = 13;
const DEFAULT_FOCUS = 34;

function ready(fn: () => void) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}

ready(() => {
  const root = document.getElementById("pt-demo");
  if (!root) return;

  const meta: Embedded = JSON.parse(
    document.getElementById("pt-record")!.textContent!,
  );
  const original = b64ToBytes(meta.record_b64);
  const pub = hexToBytes(meta.public_key_hex);

  const statusEl = root.querySelector<HTMLElement>("[data-pt-status]")!;
  const hexEl = root.querySelector<HTMLElement>("[data-pt-hex]")!;
  const verifyBtn = root.querySelector<HTMLButtonElement>("[data-pt-verify]")!;
  const tamperBtn = root.querySelector<HTMLButtonElement>("[data-pt-tamper]")!;
  const resetBtn = root.querySelector<HTMLButtonElement>("[data-pt-reset]")!;

  let working = original.slice();
  let tamperOffset: number | null = null;

  const renderHex = () => {
    // Show a compact window of the record, centred on the flipped byte.
    const focus = tamperOffset ?? DEFAULT_FOCUS;
    const start = Math.max(0, Math.min(focus - 6, working.length - HEX_WINDOW));
    const slice = working.slice(start, start + HEX_WINDOW);
    hexEl.innerHTML = Array.from(slice, (byteVal, i) => {
      const hex = byteVal.toString(16).padStart(2, "0");
      const isFlipped = tamperOffset !== null && start + i === tamperOffset;
      return isFlipped ? `<mark>${hex}</mark>` : hex;
    }).join(" ");
  };

  const setStatus = (state: "idle" | "ok" | "fail" | "busy", html: string) => {
    statusEl.dataset.state = state;
    statusEl.innerHTML = html;
  };

  const verify = () => {
    setStatus("busy", "Verifying in your browser…");
    // Yield so the busy state paints before the (synchronous) verify runs.
    setTimeout(() => {
      try {
        const { events } = verifyRun(working, pub);
        setStatus(
          "ok",
          `<strong>✓ Integrity verified</strong>
           <ul>
             <li>COSE_Sign1 signature valid (<span class="pt-mono">Ed25519</span>)</li>
             <li>${events.length} events rebuild the signed Merkle root (RFC 6962)</li>
             <li>signed by <span class="pt-mono">${meta.key_id}</span></li>
           </ul>
           <p class="pt-note">Ran the real <span class="pt-mono">provetrail@${verifierVersion}</span> package in your browser. No server. Nothing uploaded.</p>`,
        );
      } catch (e) {
        const msg = e instanceof VerifyError ? e.message : (e as Error).message;
        setStatus(
          "fail",
          `<strong>✗ Not verified</strong>
           <p>${msg}</p>
           <p class="pt-note">One byte changed and the proof breaks. That is tamper-evidence: the record cannot be quietly rewritten.</p>`,
        );
      }
    }, 60);
  };

  const tamper = () => {
    working = original.slice();
    // A different random byte every time: there is no safe place to tamper.
    tamperOffset = Math.floor(Math.random() * working.length);
    working[tamperOffset] ^= 0x01;
    renderHex();
    setStatus(
      "idle",
      `Byte ${tamperOffset} of ${working.length} flipped. Now verify it.`,
    );
  };

  const reset = () => {
    working = original.slice();
    tamperOffset = null;
    renderHex();
    setStatus("idle", "Record restored. Verify it again.");
  };

  verifyBtn.addEventListener("click", verify);
  tamperBtn.addEventListener("click", tamper);
  resetBtn.addEventListener("click", reset);

  renderHex();
  setStatus("idle", "Click verify to check this record in your browser.");
});
